import * as React from 'react';

import { cn } from './lib/utils';
import { useFlatUI } from './flat-ui';

interface TextAreaProps extends React.ComponentProps<'textarea'> {
  fullWidth?: boolean;
  helperText?: React.ReactNode;
  label?: React.ReactNode;
  error?: boolean;
  required?: boolean;
  minRows?: number;
  maxRows?: number;
  variant?: 'filled' | 'outlined' | 'standard';
  size?: 'small' | 'medium';
  draggable?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  id?: string;
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  value?: string | number | readonly string[];
  type?: 'text' | 'password' | 'email' | 'tel' | 'url' | 'time' | 'date';
}

const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      className,
      fullWidth = false,
      helperText,
      label,
      error = false,
      required = false,
      minRows,
      maxRows,
      variant = 'outlined',
      size = 'medium',
      draggable = false,
      autoFocus,
      disabled,
      id,
      type = 'text',
      ...props
    },
    ref
  ) => {
    const flat = useFlatUI();
    return (
      <div
        className={cn(
          'group relative flex flex-col gap-1',
          fullWidth && 'w-full'
        )}
      >
        <textarea
          className={cn(
            'flex h-[52px] min-h-[52px] rounded-md border-2 border-muted-foreground/20 bg-transparent px-3 text-base shadow-sm placeholder:text-base focus:placeholder:text-muted-foreground/40 placeholder:text-transparent focus-visible:outline-none focus-visible:ring-1 disabled:cursor-not-allowed disabled:opacity-50 pt-4 pb-2',
            flat && 'shadow-none',
            'peer',
            !draggable && 'resize-none',
            error
              ? 'border-destructive focus-visible:ring-destructive'
              : 'focus-visible:ring-primary focus-visible:ring-2',
            fullWidth && 'w-full',
            size === 'small' && 'text-sm py-1',
            className
          )}
          placeholder={props.placeholder}
          {...props}
        />
        {label && (
          <label
            htmlFor={id}
            className={cn(
              'absolute left-3 transition-all duration-200',
              'pointer-events-none text-muted-foreground/80',
              'peer-focus:text-[12px] peer-focus:-translate-y-4 peer-focus:translate-x-0',
              'peer-focus:top-1 peer-[&:not(:placeholder-shown)]:text-[12px] peer-[&:not(:placeholder-shown)]:-translate-y-4',
              'peer-[&:not(:placeholder-shown)]:top-1',
              'top-1/2 -translate-y-1/2 text-base',
              'bg-background px-2 -mx-1',
              !error && 'peer-focus:text-primary',
              error && 'text-destructive',
              required &&
                'after:content-["*"] after:ml-0.5 after:text-destructive'
            )}
          >
            {label}
          </label>
        )}
        {helperText && (
          <p
            className={cn(
              'text-sm text-muted-foreground',
              error && 'text-destructive'
            )}
          >
            {helperText}
          </p>
        )}
      </div>
    );
  }
);
TextArea.displayName = 'TextArea';

export { TextArea };
